/**
 * Session storage composable for the Playground feature
 * Manages persistence of playground state in browser session storage
 */

import { ref, watch } from 'vue';
import type { PlaygroundSession } from '@/features/playground/types';

// Bump on breaking schema changes so old sessions are ignored rather than
// migrated. Bumped v2→v3 when ChatCompletionSettings gained seed/stop/
// responseFormat/tools/reasoningEffort/thinkingBudget fields. Bumped v3→v4 when
// the chat/text/vision sections went: those tabs persist to the conversation
// store now, so a stale v3 session would carry chat messages nothing reads.
const SESSION_KEY = 'playground_session_v4';

/**
 * Composable for managing playground session storage
 * Provides functions to load, save, and clear session data
 * Automatically saves changes to session storage
 */
export function useSessionStorage() {
  const session = ref<PlaygroundSession | null>(null);

  /**
   * Load session data from session storage
   * Handles JSON parse errors gracefully by clearing corrupted data
   */
  const loadSession = (): PlaygroundSession | null => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Convert timestamp strings back to Date objects
        if (parsed.timestamp) {
          parsed.timestamp = new Date(parsed.timestamp);
        }
        if (parsed.modelsLoadedAt) {
          parsed.modelsLoadedAt = new Date(parsed.modelsLoadedAt);
        }


        session.value = parsed;
        return parsed;
      }
    } catch (error) {
      console.error('Failed to parse session storage:', error);
      // Clear corrupted session data
      sessionStorage.removeItem(SESSION_KEY);
    }
    return null;
  };

  /**
   * Save session data to session storage
   * @param data - The playground session data to save
   */
  const saveSession = (data: PlaygroundSession): void => {
    session.value = data;
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save session to storage:', error);
    }
  };

  /**
   * Clear all session data from storage
   */
  const clearSession = (): void => {
    session.value = null;
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.error('Failed to clear session storage:', error);
    }
  };

  /**
   * Create an empty session with default values
   */
  const createEmptySession = (): PlaygroundSession => {
    return {
      cachedModels: [],
      modelsLoadedAt: undefined,
      audio: {
        ttsText: '',
        ttsSelectedModel: '',
        ttsVoice: 'alloy',
        ttsSpeed: 1.0,
        ttsResponseFormat: 'mp3',
        ttsInstructions: '',
        ttsLanguageCode: '',
        sttSelectedModel: '',
        sttTranscription: '',
        sttLanguage: '',
        sttPrompt: '',
        sttResponseFormat: 'json',
        sttTemperature: 0,
        sttTimestampGranularities: [],
        sttStream: false,
        sttChunkingStrategy: null,
      },
      embeddings: {
        inputText: '',
        selectedModel: '',
        embeddings: null,
        dimensions: null,
      },
      moderation: {
        inputText: '',
        selectedModel: '',
        results: null,
      },
      video: {
        selectedModel: '',
      },
      activeTab: 'text',
      timestamp: new Date(),
    };
  };

  // Auto-save on changes
  watch(
    session,
    newSession => {
      if (newSession) {
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
        } catch (error) {
          console.error('Failed to auto-save session:', error);
        }
      }
    },
    { deep: true }
  );

  return {
    session,
    loadSession,
    saveSession,
    clearSession,
    createEmptySession,
  };
}
